import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import { useAppSelector } from '../../app/hooks';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import useAIStatusBand from '../CustomerPulse/useAIStatusBand.hooks';

const useStyles = makeStyles<Theme>(() => ({
  // Full-bleed strip spanning the whole console, directly under the app bar and
  // above the three panes. Same visual language as the folded-in Copilot band it
  // replaces (shared 8px inset, hitl3 body size, medium weight) — only the width
  // and the position changed.
  band: {
    flexShrink: 0,
    padding: HITL3_PADDING,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  // One line: truncate with an ellipsis (no wrap, no h-scroll). minWidth:0 lets
  // this flex child shrink so the ellipsis engages; the full message shows on
  // hover via the title attribute on the band. At 1024px the longest escalation
  // reason still fits, but the ellipsis is the guarantee that it never wraps the
  // strip to two lines and shoves the panes down.
  text: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Takeover: a hard square-wave flash between two reds with bold white text —
  // a "must not miss" alert, so intentionally NOT a soft tint and NOT a fade.
  //
  // HITL-1661 changed three things about the 3.0 throb this replaces:
  //
  //   what          3.0                     7.0                  why
  //   ────────────  ──────────────────────  ───────────────────  ──────────────
  //   period        1s                      0.7s                 faster reads as
  //                                                              more urgent
  //   easing        ease-in-out (a fade)    steps(1) (a switch)  a fade spends
  //                                                              most of its
  //                                                              cycle mid-tone,
  //                                                              which is what
  //                                                              made it easy to
  //                                                              miss
  //   weight        inherited (500)         700                  ditto
  //
  // The express countdown throbs at ~4s (HITL-1662), so the two no longer share
  // a rhythm — that collision is why neither previously read as urgent.
  //
  // The bright end is requestStaff — the same red as the Staff button and as the
  // band's own escalation state — so a take-over reads as the same red family
  // the rest of the screen uses for "a human is needed", just moving.
  //
  // It also happens to flash harder than the alternatives. Luminance ratio
  // between the two ends: requestStaff↔#6b0000 = 3.3:1, against 2.2:1 for the
  // guardsmanRed pairing this replaced. Consistency and visibility pulled the
  // same way here, which is not usually the case.
  //
  // 0.7s is ~1.4 flashes/sec, well under the WCAG 2.3.1 three-flash threshold,
  // and both ends stay red — no high-luminance strobe.
  //
  // The trade: white on requestStaff is 3.9:1, under the 4.5:1 body-text bar,
  // for the bright half of the cycle (the dark half is 12.9:1). That is the
  // exact pairing the Staff button already ships, so this matches the product's
  // existing standard rather than introducing a new one — but it is a real
  // trade, not a free win.
  //
  // Static $keyframes reference (JSS resolves the scoped name only in a static
  // rule).
  '@keyframes statusTakeoverFlash': {
    '0%, 49.999%': { backgroundColor: Colors.requestStaff },
    '50%, 100%': { backgroundColor: '#6b0000' },
  },
  //
  // ── Why there is NO prefers-reduced-motion rule here ────────────────────────
  // Deliberate, and matching the same decision recorded in the legacy
  // AIStatusBar. A take-over is a critical operational alert: the agent has to
  // act on it within seconds or the order is affected. Anything that can switch
  // it off is a way for it to be missed, and we cannot ask a restaurant's
  // agents to audit their OS accessibility settings before a shift.
  //
  // A build that DID honour it was tested and rejected: the static red read as
  // a broken screen rather than an alarm — the reviewer's first reaction was
  // "no blinking motion is being detected - something went wrong."
  //
  // It is also the wrong setting to apply. prefers-reduced-motion addresses
  // vestibular discomfort caused by MOVEMENT — parallax, sliding, zooming.
  // Nothing moves here; only a background colour changes. The criterion that
  // governs flashing is WCAG 2.3.1's three-flash threshold, and at ~1.4
  // flashes/sec this sits well inside it.
  takeover: {
    color: Colors.white,
    fontWeight: 700,
    animation: '$statusTakeoverFlash 0.7s steps(1, end) infinite',
  },
}));

/**
 * AI STATUS BAND — full console width (HITL-1660, hitl_7_0).
 *
 * Under hitl_3_0 this band is the Copilot card's footer, pinned inside the left
 * pane, where it is only as wide as that pane and easy to miss. Under hitl_7_0
 * it moves here: one strip under the header, spanning all three panes, so the
 * agent's state is visible wherever the operator is looking.
 *
 * The precedence ladder, the four status states, the pure-HITL gate and the
 * colours all live in useAIStatusBand and are shared with the 3.0 placement.
 * With the flag off this renders null and CustomerPulse keeps its footer, so
 * the two placements can never both show.
 *
 * HITL-1661 then changed two things about the escalated states, both 7.0-only:
 * the take-over alert became a hard 0.7s flash in bold (see `takeover` below),
 * and an agent interception now clears the band entirely instead of parking a
 * static "Agent Intercept" strip there (see useAIStatusBand). Staff
 * intervention is exempt from that clear-out — it outranks intercept and the
 * band is the only place it is announced.
 */
const AIStatusBand = () => {
  const classes = useStyles();
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const { show, message, background, textColor, blink } = useAIStatusBand();

  // Flag off → the band stays in the Copilot card (CustomerPulse renders it).
  // Pure-HITL restaurant → no AI band at all, matching the legacy AIStatusBar.
  // Either way the strip collapses completely rather than leaving an empty row.
  if (!isHitl7 || !show) return null;

  return (
    <div
      data-testid="ai-status-band-full-width"
      data-blink={blink}
      className={`${classes.band}${blink ? ` ${classes.takeover}` : ''}`}
      style={
        blink ? undefined : { backgroundColor: background, color: textColor }
      }
      title={message}
    >
      <span className={classes.text}>{message}</span>
    </div>
  );
};

export default AIStatusBand;
